/**
 * IndexAllocationRow.tsx
 * Item 5.2 — one TaskType's pooled IndexAllocation row on the Phase-3
 * ("Index tests") tab: TaskType name, TestCount (read-only, quote-derived
 * per I19), StartDate/EndDate window pickers (I11 window-only editing),
 * computed working-day count, per-day fractional spread (I10), and %
 * contribution against the type's IndexTestsPerDay capacity (I4).
 *
 * Window edits commit immediately on change (mirrors Phase1TaskRow's Start
 * Date cell) via the parent's `onPatchWindow` — the parent
 * (IndexTestsPanel) owns the optimistic list update + revert-on-error +
 * error banner (house pattern, Phase1Panel/Phase2Panel). This row only keeps
 * a local input buffer so the date fields feel responsive while the request
 * is in flight, and shows the FE-mirrored math (indexRowMath.ts) instantly —
 * a zero-working-day/invalid-range window is flagged INLINE on the date
 * pickers themselves (field-level `error`/`helperText`, I22) before the PATCH
 * round-trip even resolves, not just in the "Working days" column tooltip or
 * the panel's generic error banner (that banner still exists for a server-side
 * 400/409 the FE mirror didn't already catch, e.g. a stale window).
 *
 * Item 5.4 — Compare-to-Baseline ghosting (I16): when the parent supplies a
 * `baselineDiff` (compare mode ON + a still-editable draft), a faint italic
 * ghost sublabel appears under TestCount and/or the date window — SAME visual
 * language as Phase2WeeklyCell's `BaselineLabel` ("also in baseline": disabled
 * text color, 0.65 opacity, italic, 0.62rem) — but reporting the actual
 * baseline value since these are numeric/date fields, not a presence flag.
 * Only rendered where the value actually differs from baseline; a TaskType
 * with no baseline row at all reads as "new since baseline" instead.
 */

import React, { useEffect, useState } from 'react';
import { Chip, CircularProgress, TableCell, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import type { IndexAllocation, PatchIndexAllocationRequest } from '../../../../../services/scheduleIndex';
import { contributionBand, evenSpreadPerDay, rowContributionPct } from './indexRowMath';
import type { RowBaselineDiff } from './indexBaselineDiff';

export interface IndexAllocationRowProps {
  allocation: IndexAllocation;
  /** ISO stat-holiday dates in the visible window (region-aware, I7). */
  holidays: readonly string[];
  /** Submitted/Archived scenario — window pickers become read-only. */
  locked: boolean;
  /** Persist a window edit. Parent handles the optimistic update + revert-on-error. */
  onPatchWindow: (key: string, data: PatchIndexAllocationRequest) => Promise<void>;
  /** Item 5.4 — this row's BASELINE comparison, or null/undefined when compare mode is off. */
  baselineDiff?: RowBaselineDiff | null;
}

/** Faint "also in baseline" ghost value — mirrors Phase2WeeklyCell's BaselineLabel. */
const BaselineGhostLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontSize: '0.62rem',
      color: 'text.disabled',
      opacity: 0.65,
      mt: 0.25,
      fontStyle: 'italic',
    }}
  >
    {children}
  </Typography>
);

const BAND_TOKEN = {
  low: 'heatmapLow',
  mid: 'heatmapMid',
  high: 'heatmapHigh',
} as const;

/** Short field-level error text (I22) — the full explanation still lives in the Working-days tooltip. */
function shortWindowErrorText(error: 'INVALID_RANGE' | 'ZERO_WORKING_DAYS' | null): string | undefined {
  if (error === 'ZERO_WORKING_DAYS') return 'No working days in this window';
  if (error === 'INVALID_RANGE') return 'End date is before start date';
  return undefined;
}

export const IndexAllocationRow: React.FC<IndexAllocationRowProps> = ({
  allocation,
  holidays,
  locked,
  onPatchWindow,
  baselineDiff,
}) => {
  const theme = useTheme();

  const [startInput, setStartInput] = useState(allocation.StartDate);
  const [endInput, setEndInput] = useState(allocation.EndDate);
  const [saving, setSaving] = useState(false);

  // Sync local buffers when the parent's row data changes (server confirm,
  // or a revert after a failed PATCH) — mirrors Phase1TaskRow.
  useEffect(() => {
    setStartInput(allocation.StartDate);
    setEndInput(allocation.EndDate);
  }, [allocation.StartDate, allocation.EndDate]);

  const commit = async (nextStart: string, nextEnd: string) => {
    if (locked || !nextStart || !nextEnd) return;
    if (nextStart === allocation.StartDate && nextEnd === allocation.EndDate) return;
    setSaving(true);
    try {
      await onPatchWindow(allocation.IndexAllocationKey, { StartDate: nextStart, EndDate: nextEnd });
    } finally {
      setSaving(false);
    }
  };

  // FE mirror of the backend spread (indexCapacity.ts is the source of truth —
  // see indexRowMath.ts header) computed off the LIVE input buffer, so the
  // per-day/% columns update the instant the planner picks a date, without
  // waiting on the PATCH round-trip.
  const spread = evenSpreadPerDay(allocation.TestCount, startInput, endInput, holidays);
  const contribution = spread.ok
    ? rowContributionPct(spread.perDayCount, allocation.IndexTestsPerDay ?? null)
    : null;
  const band = contribution ? contributionBand(contribution.pct) : 'low';
  const bandColor = theme.schedule[BAND_TOKEN[band]];

  // I22 — field-level inline error on the date pickers themselves (not just the
  // "Working days" column tooltip / the panel's generic banner).
  const fieldErrorText = shortWindowErrorText(spread.error);

  return (
    <TableRow hover>
      {/* TaskType name */}
      <TableCell>
        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem' }}>
          {allocation.TaskTypeName ?? 'Index test'}
        </Typography>
        <Chip
          label={allocation.Status}
          size="small"
          sx={{
            mt: 0.25,
            fontSize: '0.62rem',
            height: 16,
            backgroundColor: alpha(
              allocation.Status === 'Committed' ? theme.schedule.committed : theme.schedule.tentative,
              0.14,
            ),
            color: allocation.Status === 'Committed' ? theme.schedule.committed : theme.schedule.tentative,
          }}
        />
      </TableCell>

      {/* TestCount — read-only, quote-derived (I3/I19) */}
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
          {allocation.TestCount}
        </Typography>
        {/* Item 5.4 — Compare-to-Baseline ghost */}
        {baselineDiff && !baselineDiff.hasBaseline && (
          <BaselineGhostLabel>new since baseline</BaselineGhostLabel>
        )}
        {baselineDiff?.hasBaseline && baselineDiff.testCountChanged && (
          <BaselineGhostLabel>baseline: {baselineDiff.baselineTestCount}</BaselineGhostLabel>
        )}
      </TableCell>

      {/* Start Date (editable window pickers, I11) */}
      <TableCell align="center">
        <TextField
          size="small"
          type="date"
          value={startInput}
          disabled={locked}
          error={Boolean(fieldErrorText)}
          helperText={fieldErrorText}
          onChange={(e) => {
            const next = e.target.value;
            setStartInput(next);
            if (next) void commit(next, endInput);
          }}
          onBlur={() => void commit(startInput, endInput)}
          inputProps={{ style: { fontSize: '0.78rem', padding: '4px 6px', width: 110 } }}
          FormHelperTextProps={{ sx: { fontSize: '0.6rem', width: 110, whiteSpace: 'normal', m: 0, mt: 0.25 } }}
          sx={{ '& .MuiInputBase-input': { textAlign: 'center' } }}
        />
        {/* Item 5.4 — Compare-to-Baseline ghost (window) */}
        {baselineDiff?.hasBaseline && baselineDiff.windowChanged && (
          <BaselineGhostLabel>baseline: {baselineDiff.baselineStartDate}</BaselineGhostLabel>
        )}
      </TableCell>

      {/* End Date */}
      <TableCell align="center">
        <TextField
          size="small"
          type="date"
          value={endInput}
          disabled={locked}
          error={Boolean(fieldErrorText)}
          onChange={(e) => {
            const next = e.target.value;
            setEndInput(next);
            if (next) void commit(startInput, next);
          }}
          onBlur={() => void commit(startInput, endInput)}
          inputProps={{ style: { fontSize: '0.78rem', padding: '4px 6px', width: 110 } }}
          sx={{ '& .MuiInputBase-input': { textAlign: 'center' } }}
        />
        {saving && <CircularProgress size={12} sx={{ ml: 1 }} />}
        {/* Item 5.4 — Compare-to-Baseline ghost (window) */}
        {baselineDiff?.hasBaseline && baselineDiff.windowChanged && (
          <BaselineGhostLabel>baseline: {baselineDiff.baselineEndDate}</BaselineGhostLabel>
        )}
      </TableCell>

      {/* Working days (computed, holiday-aware) */}
      <TableCell align="center">
        {spread.ok ? (
          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
            {spread.workingDays}
          </Typography>
        ) : (
          <Tooltip title={spread.message ?? 'Invalid window'}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.78rem',
                color: theme.schedule.conflictHard,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.4,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 14 }} />
              0
            </Typography>
          </Tooltip>
        )}
      </TableCell>

      {/* Per-day fractional spread (I10) */}
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
          {spread.ok ? `${spread.perDayCount.toFixed(2)} / day` : '—'}
        </Typography>
      </TableCell>

      {/* % contribution against this TaskType's IndexTestsPerDay capacity (I4) */}
      <TableCell align="center">
        {contribution ? (
          <Tooltip
            title={
              contribution.usedFallbackCapacity
                ? `No capacity set for this type yet — assuming 8/day (admin can set IndexTestsPerDay in Test Definitions).`
                : `${spread.perDayCount.toFixed(2)} tests/day ÷ ${allocation.IndexTestsPerDay}/day capacity`
            }
          >
            <Chip
              label={`${contribution.pct}%${contribution.usedFallbackCapacity ? ' *' : ''}`}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                backgroundColor: alpha(bandColor, 0.16),
                color: band === 'high' ? theme.schedule.conflictHard : bandColor,
              }}
            />
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};
